<script setup>
import { marked } from 'marked'
import { computed, nextTick, ref, shallowRef, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { useAnalytics } from '@/shared/analytics'
import { useBradleyEnvironment } from '@/shared/composables/use-bradley-environment.js'
import { BRADLEY_ANALYTICS_EVENTS } from '../analytics/bradley-analytics.constants.js'
import { bradleySourcesModalAnalyticsPayload } from '../analytics/bradley-analytics.telemetry.js'
import { useBradleyComposerAttachments } from '../composables/use-bradley-composer-attachments.js'
import { useBradleyComposerRecorder } from '../composables/use-bradley-composer-recorder.js'
import { sendBradleyFeedback } from '../services/bradley-ai.service.js'
import { areAllBradleyAttachmentsReadyForSend, BRADLEY_ATTACHMENT_STATUS, isBradleyAttachmentReadyForSend } from '../utils/bradley-attachment.util.js'
import { buildCitationMap, citationMapFromDocuments, citationTooltip } from '../utils/bradley-citations.js'
import { messageHasBradleySources } from '../utils/bradley-sources.js'
import { resolveBradleyStepLabel } from '../utils/bradley-steps.js'
import BradleyComposerRecorderPreview from './bradley-composer-recorder-preview.vue'
import BradleyDeleteChatModal from './bradley-delete-chat-modal.vue'
import BradleyDislikeModal from './bradley-dislike-modal.vue'
import BradleyPlotlyVisualization from './bradley-plotly-visualization.vue'
import BradleyPlusMenu from './bradley-plus-menu.vue'
import BradleyReportModal from './bradley-report-modal.vue'
import BradleyStagedAttachments from './bradley-staged-attachments.vue'
import BradleyUploadErrorBanner from './bradley-upload-error-banner.vue'

const props = defineProps({
  file: {
    type: Object,
    default: null,
  },
  message: {
    type: String,
    default: '',
  },
  selectedTools: {
    type: Array,
    default: () => [],
  },
  messages: {
    type: Array,
    default: () => [],
  },
  loadingMessages: {
    type: Boolean,
    default: false,
  },
  awaitingResponse: {
    type: Boolean,
    default: false,
  },
  isStreaming: {
    type: Boolean,
    default: false,
  },
  streamingContent: {
    type: String,
    default: '',
  },
  currentStep: {
    type: Object,
    default: null,
  },
  streamingCitations: {
    type: Array,
    default: () => [],
  },
  conversationId: {
    type: [String, Number],
    default: null,
  },
  deleting: {
    type: Boolean,
    default: false,
  },
  uploadedDocuments: {
    type: Array,
    default: () => [],
  },
  selectedUploadedDocuments: {
    type: Array,
    default: () => [],
  },
  uploading: {
    type: Boolean,
    default: false,
  },
  projectId: {
    type: String,
    default: null,
  },
  projectTitle: {
    type: String,
    default: '',
  },
  projectFiles: {
    type: Array,
    default: null,
  },
})

const emit = defineEmits(['exit', 'send', 'selectTool', 'removeTool', 'delete', 'fileUploaded', 'update:selectedUploadedDocuments', 'showToast', 'stopStream', 'reuseProjectFiles'])

const { t, locale } = useI18n()
const { trackEvent } = useAnalytics()
const authStore = useAuthStore()

const userMessage = ref('')
const messagesContainer = ref(null)
const modalSource = ref(false)
const reportModalOpen = ref(false)
const selectedMessageForReport = ref(null)
const deleteModalOpen = ref(false)
const dislikeModalOpen = ref(false)
const selectedMessageForDislike = ref(null)
const selectedMessageIndexForDislike = ref(null)
const activeSourceMessage = ref(null)
const messageFeedback = ref(new Map())
const feedbackMessages = ref(new Map())
const copiedMessageId = ref(null)
const textareaReference = ref(null)
const plusMenuReference = ref(null)
const expandedMessages = ref(new Set())
const typePromptTracked = ref(false)
const { documentUploadEnabled: DOCUMENT_UPLOAD_ENABLED, newFileUploadEnabled: NEW_FILE_UPLOAD_ENABLED, projectsEnabled: PROJECTS_ENABLED } = useBradleyEnvironment()
const fileInputId = useId()
const messageInputId = useId()
const projectFilesUtil = shallowRef(null)
const projectAnalytics = shallowRef(null)
if (PROJECTS_ENABLED) {
  import('../utils/bradley-project-files.util.js').then((module) => {
    projectFilesUtil.value = module
  })
  import('../analytics/bradley-project-analytics.telemetry.js').then((module) => {
    projectAnalytics.value = module
  })
}

function getBradleyProjectFileId(file) {
  return projectFilesUtil.value?.getBradleyProjectFileId?.(file) || ''
}
function getBradleyProjectFileName(file) {
  return projectFilesUtil.value?.getBradleyProjectFileName?.(file) || ''
}
function getBradleyProjectFileSizeBytes(file) {
  return projectFilesUtil.value?.getBradleyProjectFileSizeBytes?.(file) || 0
}
function getCachedBradleyProjectFiles(projectId) {
  return projectFilesUtil.value?.getCachedBradleyProjectFiles?.(projectId) || []
}

function trackProjectPromptEvent(eventName, {
  action,
  promptId = null,
  responseId = null,
  toolName = null,
  status = null,
} = {}) {
  if (!props.projectId) {
    return
  }

  const buildPayload = projectAnalytics.value?.bradleyProjectPromptAnalyticsPayload
  if (!buildPayload) {
    return
  }

  trackEvent(eventName, buildPayload({
    action,
    projectId: props.projectId,
    chatId: props.conversationId,
    userId: authStore?.userData?.id ?? null,
    promptId,
    responseId,
    toolName,
    triggerSource: 'project_chat_thread',
    status,
  }))
}

function handleChatComposerInput() {
  autoResizeTextarea()
  if (!typePromptTracked.value && userMessage.value.trim().length > 0) {
    typePromptTracked.value = true
    trackEvent(BRADLEY_ANALYTICS_EVENTS.TYPE_PROMPT, { surface: 'chat_thread' })
  }
}

function autoResizeTextarea() {
  if (textareaReference.value) {
    textareaReference.value.style.height = 'auto'
    const newHeight = textareaReference.value.scrollHeight
    const maxHeight = 200

    if (newHeight >= maxHeight) {
      textareaReference.value.style.height = `${maxHeight}px`
      textareaReference.value.style.overflowY = 'auto'
    }
    else {
      textareaReference.value.style.height = `${newHeight}px`
      textareaReference.value.style.overflowY = 'hidden'
    }
  }
}

const selectedDocumentsModel = computed({
  get: () => props.selectedUploadedDocuments,
  set: value => emit('update:selectedUploadedDocuments', value),
})

const resolvedProjectFiles = computed(() => {
  if (Array.isArray(props.projectFiles)) {
    return props.projectFiles
  }
  if (!props.projectId) {
    return []
  }
  return getCachedBradleyProjectFiles(props.projectId)
})

function handleReuseProjectFiles(files) {
  const selected = Array.isArray(files) ? files : []
  if (!selected.length) {
    return
  }

  const nextAttachments = [...(selectedDocumentsModel.value || [])]

  selected.forEach((file) => {
    const fileId = getBradleyProjectFileId(file)
    const alreadySelected = nextAttachments.some(
      item => item?.file_id === fileId
        || (file.file_hash && item?.file_hash === file.file_hash)
        || item?.file_name === getBradleyProjectFileName(file),
    )
    if (alreadySelected) {
      return
    }

    nextAttachments.push({
      client_id: fileId || `project-file-${Date.now()}`,
      file_id: fileId,
      file_name: getBradleyProjectFileName(file),
      size: getBradleyProjectFileSizeBytes(file),
      file_size: getBradleyProjectFileSizeBytes(file),
      file_hash: file.file_hash || `mock-hash-${fileId}`,
      mime_type: file.mime_type || null,
      is_image: String(file.mime_type || '').startsWith('image/')
        || /\.(?:png|jpe?g|gif|webp)$/i.test(getBradleyProjectFileName(file)),
      preview_url: null,
      upload_status: BRADLEY_ATTACHMENT_STATUS.SUCCESS,
      source: 'project_files',
    })
  })

  selectedDocumentsModel.value = nextAttachments
  emit('reuseProjectFiles', selected)
}

const {
  acceptAttribute,
  dismissUploadError,
  fileInput,
  handleFileSelected,
  handlePaste,
  isDragging,
  onComposerDragLeave,
  onComposerDragOver,
  onComposerDrop,
  openFilePickerFromMenu,
  removeSelectedUploadedDocument,
  uploadErrors,
} = useBradleyComposerAttachments({
  documentUploadEnabled: DOCUMENT_UPLOAD_ENABLED,
  newFileUploadEnabled: NEW_FILE_UPLOAD_ENABLED,
  selectedDocumentsModel,
  emit,
  surface: 'chat_thread',
})

const {
  isInitializing: isInitializingRecorder,
  isRecording,
  isPaused,
  isTranscribing,
  formattedDuration,
  waveformData,
  hasPreview: hasRecorderPreview,
  pauseRecording,
  resumeRecording,
  toggleRecording,
  discardRecording,
} = useBradleyComposerRecorder({
  emit,
  resolveConversationId: () => props.conversationId,
  onTranscriptReady: handleTranscriptReady,
})

const canSendMessage = computed(() => {
  if (!areAllBradleyAttachmentsReadyForSend(selectedDocumentsModel.value)) {
    return false
  }

  const hasReadyAttachments = selectedDocumentsModel.value.some(isBradleyAttachmentReadyForSend)
  return Boolean(userMessage.value.trim()) || hasReadyAttachments
})

function handleTranscriptReady(text) {
  const nextText = String(text ?? '')
  const current = String(userMessage.value ?? '').trimEnd()
  userMessage.value = current ? `${current} ${nextText}` : nextText
  nextTick(() => {
    autoResizeTextarea()
    textareaReference.value?.focus?.()
  })
}

function handleSend() {
  if (!canSendMessage.value || props.isStreaming || props.awaitingResponse || props.uploading || isRecording.value || isPaused.value || isInitializingRecorder.value || isTranscribing.value) {
    return
  }

  emit('send', userMessage.value.trim())
  userMessage.value = ''
  discardRecording()

  if (textareaReference.value) {
    textareaReference.value.style.height = 'auto'
  }
}

function handleChatKeydown(event) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    if (!canSendMessage.value) {
      trackEvent(BRADLEY_ANALYTICS_EVENTS.SEND_PROMPT_BLOCKED_NO_TEXT, {
        has_files_attached: selectedDocumentsModel.value.length > 0,
        file_count: selectedDocumentsModel.value.length,
        block_reason: 'empty_prompt',
      })
      return
    }
    if (props.uploading) {
      return
    }
    handleSend()
  }
}

function handleSelectTool(tool) {
  emit('selectTool', tool)
}

function handleRemoveTool(tool) {
  emit('removeTool', tool)
}

function handleOpenModal(messageItem) {
  activeSourceMessage.value = messageItem ?? null
  modalSource.value = true

  trackEvent(BRADLEY_ANALYTICS_EVENTS.SOURCES_MODAL_OPENED, bradleySourcesModalAnalyticsPayload(
    messageItem,
    props.messages,
    props.conversationId,
  ))
}
function sourceModalClose() {
  modalSource.value = false
  activeSourceMessage.value = null
}

function handleOpenReportModal(messageItem) {
  selectedMessageForReport.value = messageItem
  reportModalOpen.value = true
}

function reportModalClose() {
  reportModalOpen.value = false
  selectedMessageForReport.value = null
}

let lastReportToastAt = 0
const REPORT_TOAST_DEBOUNCE_MS = 1500

function handleReportSuccess() {
  const now = Date.now()
  if (now - lastReportToastAt < REPORT_TOAST_DEBOUNCE_MS) {
    return
  }
  lastReportToastAt = now

  const date = new Date()
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const dateString = `${day}/${month}/${year}`
  emit('showToast', {
    severity: 'success',
    summary: t('newBradley.toasts.successTitle'),
    life: 3000,
    reportContent: {
      main: t('newBradley.toasts.reportSuccess'),
      secondary: t('newBradley.toasts.reportSuccessMessage'),
      date: t('newBradley.toasts.reportSuccessDate', { date: dateString }),
    },
  })
}

function handleOpenDeleteModalOpen() {
  deleteModalOpen.value = true
}

function closeDeleteModalOpen() {
  deleteModalOpen.value = false
}

async function handleCopyMessage(messageItem) {
  const htmlContent = messageItem?.htmlContent ?? ''
  const plainContent = messageItem?.content || messageItem?.text || ''
  if (!htmlContent && !plainContent)
    return

  copiedMessageId.value = messageItem.key || messageItem.id
  setTimeout(() => {
    copiedMessageId.value = null
  }, 2000)

  trackProjectPromptEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_RESPONSE_COPY_CLICK, {
    action: 'copy_click',
    responseId: messageItem?.message_id || messageItem?.id || null,
    status: 'clicked',
  })

  if (!navigator.clipboard) {
    await navigator.clipboard.writeText(plainContent)
    return
  }

  try {
    if (htmlContent) {
      const htmlBlob = new Blob([htmlContent], { type: 'text/html' })
      const plainBlob = new Blob([plainContent], { type: 'text/plain' })
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': plainBlob }),
      ])
    }
    else {
      await navigator.clipboard.writeText(plainContent)
    }
  }
  catch {
    await navigator.clipboard.writeText(plainContent)
  }
}

function tableElementToCsv(tableElement) {
  if (!tableElement || typeof tableElement.querySelector !== 'function')
    return ''
  const rows = tableElement.querySelectorAll('tr')
  const csvRows = []
  for (const row of rows) {
    const cells = row.querySelectorAll('th, td')
    const values = Array.from(cells).map((cell) => {
      const text = (cell.textContent || '').trim().replace(/"/g, '""')
      return text.includes(',') || text.includes('"') || text.includes('\n') ? `"${text}"` : text
    })
    csvRows.push(values.join(','))
  }
  return csvRows.join('\n')
}

function getMessageArticle(messageKey) {
  return messagesContainer.value?.querySelector?.(`[data-message-key="${messageKey}"]`)
}

async function handleCopyHtmlTable(messageItem) {
  const article = getMessageArticle(messageItem.key)
  const table = article?.querySelector?.('.chat-message__content table')
  if (!table)
    return
  const csv = tableElementToCsv(table)
  if (!csv)
    return
  copiedMessageId.value = `table-${messageItem.key}`
  setTimeout(() => {
    copiedMessageId.value = null
  }, 2000)
  if (!navigator.clipboard)
    return
  try {
    const html = table.outerHTML
    const htmlBlob = new Blob([html], { type: 'text/html' })
    const plainBlob = new Blob([csv], { type: 'text/plain' })
    await navigator.clipboard.write([
      new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': plainBlob }),
    ])
  }
  catch {
    await navigator.clipboard.writeText(csv)
  }
}

function handleExportHtmlTable(messageItem) {
  const article = getMessageArticle(messageItem.key)
  const table = article?.querySelector?.('.chat-message__content table')
  if (!table)
    return
  const csv = tableElementToCsv(table)
  if (!csv)
    return
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `bradley-table-${messageItem.key || Date.now()}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function toggleMessageExpansion(messageKey) {
  if (expandedMessages.value.has(messageKey)) {
    expandedMessages.value.delete(messageKey)
  }
  else {
    expandedMessages.value.add(messageKey)
  }
}

async function handleLike(message, _messageIndex) {
  if (!props.conversationId || !message?.message_id) {
    return
  }

  try {
    await sendBradleyFeedback(props.conversationId, {
      message_id: message.message_id,
      user_like: true,
      categories: [],
    })

    messageFeedback.value.set(message.message_id, 'liked')
    feedbackMessages.value.set(message.message_id, t('newBradley.toasts.feedbackSuccess'))
    trackProjectPromptEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_RESPONSE_FEEDBACK_POSITIVE, {
      action: 'feedback_positive',
      responseId: message.message_id,
      status: 'submitted',
    })

    setTimeout(() => {
      feedbackMessages.value.delete(message.message_id)
    }, 2000)
  }
  catch (error) {
    console.error('Failed to send like feedback:', error)
  }
}

function handleDislike(message, messageIndex) {
  selectedMessageForDislike.value = message
  selectedMessageIndexForDislike.value = messageIndex
  dislikeModalOpen.value = true
}

function handleDislikeSuccess() {
  if (selectedMessageForDislike.value?.message_id) {
    messageFeedback.value.set(selectedMessageForDislike.value.message_id, 'disliked')
    feedbackMessages.value.set(selectedMessageForDislike.value.message_id, t('newBradley.toasts.feedbackSuccess'))
    trackProjectPromptEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_RESPONSE_FEEDBACK_NEGATIVE, {
      action: 'feedback_negative',
      responseId: selectedMessageForDislike.value.message_id,
      status: 'submitted',
    })

    setTimeout(() => {
      feedbackMessages.value.delete(selectedMessageForDislike.value.message_id)
    }, 2000)
  }

  dislikeModalOpen.value = false
  selectedMessageForDislike.value = null
  selectedMessageIndexForDislike.value = null
}

function handleInputFocus() {
  if (plusMenuReference.value?.closeMenu) {
    plusMenuReference.value.closeMenu()
  }
}

function closeDislikeModal() {
  dislikeModalOpen.value = false
  selectedMessageForDislike.value = null
  selectedMessageIndexForDislike.value = null
}

function escapeHtml(text = '') {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function convertContentToHtml(content) {
  if (!content) {
    return ''
  }

  try {
    let cleanContent = content.trim()

    if (cleanContent.startsWith('```markdown')) {
      cleanContent = cleanContent.replace(/^```markdown\n?/, '')
    }
    else if (cleanContent.startsWith('```')) {
      cleanContent = cleanContent.replace(/^```\n?/, '')
    }

    if (cleanContent.endsWith('```')) {
      cleanContent = cleanContent.replace(/\n?```$/, '')
    }

    marked.setOptions({
      sanitize: false,
      gfm: true,
      breaks: true,
      smartLists: true,
      smartypants: true,
    })

    const htmlContent = marked(cleanContent)

    return htmlContent
  }
  catch (error) {
    console.warn('Error parsing markdown:', error)
    return `<p>${escapeHtml(content)}</p>`
  }
}

function renderCitationMarkers(html, citationMap) {
  if (!html || !citationMap?.size) {
    return html
  }

  return html.replace(/\[(\d{1,3})\]/g, (marker, digits) => {
    const citation = citationMap.get(Number(digits))
    if (!citation) {
      return marker
    }

    const title = escapeHtml(citationTooltip(citation, {
      name: t('newBradley.chatFile.citationName'),
      type: t('newBradley.chatFile.citationType'),
    }))
    const url = escapeHtml(citation.share_point_url ?? '')
    return `<button type="button" class="chat-message__citation" data-citation-url="${url}" title="${title}">${digits}</button>`
  })
}

function openCitation(event) {
  const url = event.target?.dataset?.citationUrl
  if (!url) {
    return
  }

  // The URL comes from the backend, but it ends up in window.open: anything other than a
  // plain web link is refused rather than trusted.
  try {
    const parsed = new URL(url)
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      window.open(parsed.href, '_blank', 'noopener,noreferrer')
    }
  }
  catch {
    // Not a URL at all: nothing to open.
  }
}

function parseJsonField(value) {
  if (value == null)
    return value
  if (typeof value !== 'string')
    return value
  try {
    return JSON.parse(value)
  }
  catch {
    return value
  }
}

// The backend sends the step's identity; the wording is written here, which is what puts the
// progress line in the consultant's language. An unknown tool falls back to its own name
// rather than to a blank line -- see resolveBradleyStepLabel.
const currentStepLabel = computed(() => resolveBradleyStepLabel(props.currentStep, t))

const streamingCitationMap = computed(() => buildCitationMap(props.streamingCitations))

const formattedMessages = computed(() => {
  const conversationMessages = props.messages ?? []

  return conversationMessages.map((message, index) => {
    const key = message?.message_id || `${message?.is_user ? 'user' : 'assistant'}-${index}`
    const dateValue = message?.created_at ?? message?.timestamp
    const parsedDate = dateValue ? new Date(dateValue) : null
    const displayTimestamp = parsedDate && !Number.isNaN(parsedDate.getTime())
      ? parsedDate.toLocaleTimeString(locale.value, { hour: '2-digit', minute: '2-digit' })
      : ''

    const rawHtml = renderCitationMarkers(
      message?.html_content || convertContentToHtml(message?.content ?? ''),
      citationMapFromDocuments(message?.documents),
    )
    const hasHtmlTable = typeof rawHtml === 'string' && rawHtml.toLowerCase().includes('<table')

    const plotlyFigure = parseJsonField(message?.plotly_figure)
    const hasPlotlyFigure = !!plotlyFigure && typeof plotlyFigure === 'object' && Array.isArray(plotlyFigure?.data)

    return {
      ...message,
      key,
      displayTimestamp,
      htmlContent: rawHtml,
      sourceLabel: message?.source_label || message?.source || null,
      hasPlotlyFigure,
      plotlyFigure: hasPlotlyFigure ? plotlyFigure : null,
      query: message?.query ?? null,
      hasHtmlTable,
      hasSources: messageHasBradleySources(message, conversationMessages),
    }
  })
})

const hasMessages = computed(() => formattedMessages.value.length > 0)

const conversationDateLabel = computed(() => {
  const first = props.messages?.[0]
  if (!first)
    return ''
  const dateValue = first?.created_at ?? first?.timestamp
  const parsedDate = dateValue ? new Date(dateValue) : null
  if (!parsedDate || Number.isNaN(parsedDate.getTime()))
    return ''
  return parsedDate.toLocaleDateString(locale.value, { weekday: 'long', day: 'numeric', month: 'long' })
})

watch(userMessage, (newValue) => {
  if (!String(newValue ?? '').trim()) {
    typePromptTracked.value = false
  }
})

// Sync like/dislike from backend
watch(
  () => props.messages,
  (messages) => {
    const next = new Map()
    for (const message of messages ?? []) {
      if (message?.message_id == null)
        continue
      if (message?.feedback != null && typeof message.feedback.user_like === 'boolean') {
        next.set(message.message_id, message.feedback.user_like ? 'liked' : 'disliked')
      }
      else if (messageFeedback.value.has(message.message_id)) {
        next.set(message.message_id, messageFeedback.value.get(message.message_id))
      }
    }
    messageFeedback.value = next
  },
  { deep: true },
)
</script>

<template>
  <section class="chat-file">
    <div class="chat-file__body">
      <div
        ref="messagesContainer"
        class="chat-file__messages"
      >
        <div
          v-if="loadingMessages"
          class="chat-file__messages-loader"
        >
          <i class="pi pi-spinner animate-spin" />
          <span>{{ $t('newBradley.chatFile.loadingMessages') }}</span>
        </div>

        <div
          v-else-if="!hasMessages"
          class="chat-file__messages-empty"
        >
          <i class="pi pi-comments" />
          <p>{{ $t('newBradley.chatFile.sendMessageToStart') }}</p>
        </div>

        <template v-else>
          <div class="chat-file__messages-top-bar">
            <div class="chat-file__datetime">
              {{ conversationDateLabel }}
            </div>
            <div class="chat-file__messages-top-actions">
              <button
                type="button"
                class="chat-file__link-button chat-file__link-button--danger"
                :disabled="!conversationId || deleting"
                @click="handleOpenDeleteModalOpen"
              >
                <i
                  v-if="deleting"
                  class="pi pi-spinner animate-spin"
                />
                <span
                  v-else
                  class="pi pi-trash"
                />
                {{ deleting ? $t('newBradley.chatFile.deleting') : $t('newBradley.chatFile.delete') }}
              </button>
            </div>
          </div>

          <article
            v-for="(messageItem, index) in formattedMessages"
            :key="messageItem.key"
            :data-message-key="messageItem.key"
            class="chat-message"
            :class="[
              messageItem.is_user ? 'chat-message--user' : 'chat-message--assistant',
            ]"
          >
            <div class="chat-message__inner">
              <div
                v-if="!messageItem.is_user && messageItem.sourceLabel"
                class="chat-message__meta"
              >
                <span class="chat-message__meta-label">Sourced from:</span>
                <span class="chat-message__meta-value">{{ messageItem.sourceLabel }}</span>
              </div>

              <div
                v-if="messageItem.is_user"
                class="chat-message__user-pill"
              >
                <div
                  :class="{
                    'line-clamp-1': !expandedMessages.has(messageItem.key),
                    'whitespace-pre-wrap': expandedMessages.has(messageItem.key),
                  }"
                >
                  {{ messageItem.content }}
                </div>
                <button
                  v-if="messageItem.content && messageItem.content.length > 80"
                  class="text-xs text-gray-600 hover:text-gray-800 dark:text-dss-surface-500 dark:hover:text-dss-surface-600 mt-1 flex items-center gap-1"
                  @click="toggleMessageExpansion(messageItem.key)"
                >
                  <span>{{ expandedMessages.has(messageItem.key) ? $t('newBradley.chatFile.showLess') : $t('newBradley.chatFile.showMore') }}</span>
                  <i :class="expandedMessages.has(messageItem.key) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" />
                </button>
              </div>
              <div v-else>
                <div
                  class="chat-message__content"
                  @click="openCitation"
                  v-html="messageItem.htmlContent"
                />

                <bradley-plotly-visualization
                  v-if="messageItem.hasPlotlyFigure"
                  :plotly-figure="messageItem.plotlyFigure"
                  :query="messageItem.query"
                  :message-id="messageItem.key"
                />

                <div
                  v-if="messageItem.hasHtmlTable"
                  class="flex items-center gap-4 mt-3 mb-1"
                >
                  <button
                    type="button"
                    class="chat-file__table-action text-dss-secondary-650 hover:text-dss-secondary-400"
                    :title="$t('newBradley.chatFile.exportToSheet')"
                    @click="handleExportHtmlTable(messageItem)"
                  >
                    <i class="pi pi-file-export" />
                    {{ $t('newBradley.chatFile.exportToSheet') }}
                  </button>
                  <button
                    type="button"
                    class="chat-file__table-action text-dss-secondary-650 hover:text-dss-secondary-400"
                    :title="copiedMessageId === `table-${messageItem.key}` ? $t('newBradley.chatFile.copied') : $t('newBradley.chatFile.copyTable')"
                    @click="handleCopyHtmlTable(messageItem)"
                  >
                    <i class="pi pi-clone" />
                    {{ copiedMessageId === `table-${messageItem.key}` ? $t('newBradley.chatFile.copied') : $t('newBradley.chatFile.copyTable') }}
                  </button>
                </div>

                <div class="flex gap-8 mt-4">
                  <button
                    class="cursor-pointer"
                    @click="handleCopyMessage(messageItem)"
                  >
                    <span :class="copiedMessageId === messageItem.key ? 'pi pi-check' : 'pi pi-copy'" />
                  </button>

                  <button
                    class="cursor-pointer"
                    :class="{ 'text-blue-600': messageFeedback.get(messageItem.message_id) === 'liked' }"
                    @click="handleLike(messageItem, index)"
                  >
                    <span class="pi pi-thumbs-up" />
                  </button>

                  <button
                    class="cursor-pointer"
                    :class="{ 'text-red-600': messageFeedback.get(messageItem.message_id) === 'disliked' }"
                    @click="handleDislike(messageItem, index)"
                  >
                    <span class="pi pi-thumbs-down" />
                  </button>

                  <button
                    class="cursor-pointer flex items-center gap-1"
                    :class="messageItem.report ? 'text-orange-500 hover:text-orange-600' : 'text-gray-600 hover:text-gray-800 dark:text-dss-surface-500 dark:hover:text-dss-surface-600'"
                    :title="$t('newBradley.chatFile.report')"
                    @click="handleOpenReportModal(messageItem)"
                  >
                    <span class="pi pi-flag" />
                    <span class="text-sm">{{ $t('newBradley.chatFile.report') }}</span>
                  </button>

                  <button
                    v-if="messageItem.hasSources"
                    class="cursor-pointer border border-dss-surface-200 p-1 rounded-full w-[129px] h-[35px] relative bg-[#F1F5F9] dark:bg-dss-surface-200 dark:text-dss-surface-600 color[#1E293B]"
                    @click="handleOpenModal(messageItem)"
                  >
                    <span class="bg-[#E2E8F0] w-[28px] h-[28px] rounded-full absolute left-[2px] top-[3px]" />
                    <span class="bg-[#CBD5E1] w-[28px] h-[28px] rounded-full absolute left-[15px] top-[3px]" />
                    <span class="bg-[#94A3B8] w-[28px] h-[28px] rounded-full absolute left-[30px] top-[3px]" />
                    <span class="absolute top-1 right-2">{{ $t('newBradley.sourcesModal.sources') }}</span>
                  </button>
                </div>

                <transition
                  enter-active-class="transition ease-out duration-200"
                  enter-from-class="transform opacity-0 translate-y-1"
                  enter-to-class="transform opacity-100 translate-y-0"
                  leave-active-class="transition ease-in duration-150"
                  leave-from-class="transform opacity-100 translate-y-0"
                  leave-to-class="transform opacity-0 translate-y-1"
                >
                  <div
                    v-if="feedbackMessages.get(messageItem.message_id)"
                    class="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2"
                  >
                    <i class="pi pi-check-circle text-green-600" />
                    <span class="text-sm text-green-800">{{ feedbackMessages.get(messageItem.message_id) }}</span>
                  </div>
                </transition>
              </div>

              <div
                v-if="messageItem.displayTimestamp && messageItem.is_user"
                class="chat-message__timestamp flex items-center mt-1"
              >
                <div>{{ messageItem.displayTimestamp }}</div>
              </div>
            </div>
          </article>
        </template>

        <article
          v-if="isStreaming"
          class="chat-message chat-message--assistant"
        >
          <div class="chat-message__inner">
            <!-- A step that started after the text began -- the ReAct loop can answer, then
                 call another tool. Plain text, never v-html: a label is not markup. -->
            <div
              v-if="currentStepLabel"
              class="chat-file__typing-indicator chat-file__typing-indicator--inline"
            >
              <i class="pi pi-spinner animate-spin" />
              <span>{{ currentStepLabel }}</span>
            </div>
            <div
              class="chat-message__content"
              @click="openCitation"
              v-html="renderCitationMarkers(convertContentToHtml(streamingContent), streamingCitationMap)"
            />
            <span
              class="chat-message__typing-cursor"
              aria-hidden="true"
            >▋</span>
          </div>
        </article>

        <div
          v-if="awaitingResponse && !isStreaming"
          class="chat-file__typing-indicator chat-file__typing-indicator--inline"
        >
          <i class="pi pi-spinner animate-spin" />
          <span>{{ currentStepLabel || $t('newBradley.chatFile.bradleyResponding') }}</span>
        </div>
      </div>
    </div>

    <footer class="chat-file__footer">
      <div
        class="chat-file__input-wrapper"
        :class="{ 'chat-file__input-wrapper--drag-active': DOCUMENT_UPLOAD_ENABLED && isDragging }"
        @dragover.prevent="onComposerDragOver"
        @dragleave.prevent="onComposerDragLeave"
        @drop.prevent="onComposerDrop"
      >
        <label
          v-if="DOCUMENT_UPLOAD_ENABLED"
          :for="fileInputId"
          class="sr-only"
        >{{ $t(NEW_FILE_UPLOAD_ENABLED ? 'newBradley.input.addFile' : 'newBradley.input.addFileDocuments') }}</label>
        <input
          v-if="DOCUMENT_UPLOAD_ENABLED"
          :id="fileInputId"
          ref="fileInput"
          type="file"
          multiple
          :accept="acceptAttribute"
          class="hidden"
          @change="handleFileSelected"
        >

        <div
          v-if="selectedUploadedDocuments.length > 0 || uploadErrors.length > 0"
          class="chat-file__composer-top"
        >
          <bradley-staged-attachments
            :items="selectedUploadedDocuments"
            @remove="removeSelectedUploadedDocument"
          />
          <bradley-upload-error-banner
            :errors="uploadErrors"
            @dismiss="dismissUploadError"
          />
        </div>

        <div class="chat-file__textarea-row">
          <bradley-composer-recorder-preview
            v-if="hasRecorderPreview"
            :is-recording="isRecording"
            :is-paused="isPaused"
            :is-transcribing="isTranscribing"
            :duration-label="formattedDuration"
            :waveform-data="waveformData"
            @pause="pauseRecording"
            @resume="resumeRecording"
            @discard="discardRecording"
          />
          <label
            :for="messageInputId"
            class="sr-only"
          >{{ $t('newBradley.chatFile.typeYourMessage') }}</label>
          <textarea
            v-show="!isRecording && !isPaused && !isTranscribing"
            :id="messageInputId"
            ref="textareaReference"
            v-model="userMessage"
            rows="1"
            :placeholder="$t('newBradley.chatFile.typeYourMessage')"
            :disabled="isStreaming || awaitingResponse"
            class="chat-file__textarea"
            @keydown="handleChatKeydown"
            @focus="handleInputFocus"
            @input="handleChatComposerInput"
            @paste="handlePaste"
          />
          <button
            v-if="isStreaming || awaitingResponse"
            type="button"
            class="chat-file__stop-button"
            :title="$t('newBradley.chatFile.stopStream')"
            :aria-label="$t('newBradley.chatFile.stopStream')"
            @click="emit('stopStream')"
          >
            <i class="pi pi-stop-circle" />
          </button>
        </div>

        <div class="chat-file__input-actions">
          <div class="chat-file__tools">
            <bradley-plus-menu
              v-if="!isRecording && !isPaused && !isTranscribing"
              ref="plusMenuReference"
              :project-id="projectId"
              :project-title="projectTitle"
              :conversation-id="conversationId"
              :project-files="resolvedProjectFiles"
              @add-file="openFilePickerFromMenu"
              @select-tool="handleSelectTool"
              @reuse-project-files="handleReuseProjectFiles"
            />

            <div
              v-for="tool in selectedTools"
              :key="tool"
              class="chat-file__tool-pill"
            >
              <button
                type="button"
                class="chat-file__tool-pill-remove"
                :aria-label="$t('newBradley.chatFile.removeTool', { tool })"
                :title="$t('newBradley.chatFile.removeTool', { tool })"
                @click="handleRemoveTool(tool)"
              >
                <i class="pi pi-times" />
              </button>
              <span>{{ tool }}</span>
            </div>
          </div>

          <div class="chat-file__send-actions">
            <button
              v-if="!isStreaming && !awaitingResponse"
              type="button"
              class="chat-file__voice-button"
              :class="{ 'chat-file__voice-button--recording': isRecording || isPaused }"
              :disabled="uploading || isInitializingRecorder || isTranscribing"
              :title="(isRecording || isPaused) ? $t('newBradley.input.stopRecording') : $t('newBradley.input.recordAudio')"
              :aria-label="(isRecording || isPaused) ? $t('newBradley.input.stopRecording') : $t('newBradley.input.recordAudio')"
              @click="toggleRecording"
            >
              <i :class="(isRecording || isPaused) ? 'pi pi-stop' : 'pi pi-microphone'" />
            </button>
            <button
              v-if="!isStreaming && !awaitingResponse"
              type="button"
              class="chat-file__send-button chat-file__send-button--primary"
              :disabled="!canSendMessage || uploading || isRecording || isPaused || isInitializingRecorder || isTranscribing"
              :title="$t('newBradley.chatFile.sendMessage')"
              @click="handleSend"
            >
              <i class="pi pi-arrow-up" />
            </button>
          </div>
        </div>
      </div>

      <p
        v-if="DOCUMENT_UPLOAD_ENABLED"
        class="chat-file__upload-limits"
      >
        <i
          class="pi pi-info-circle chat-file__upload-limits-icon"
          aria-hidden="true"
        />
        <span class="chat-file__upload-limits-text">{{
          $t(NEW_FILE_UPLOAD_ENABLED
            ? 'newBradley.input.uploadLimitsDisclaimer'
            : 'newBradley.input.uploadLimitsDisclaimerLegacy')
        }}</span>
      </p>
    </footer>
  </section>

  <bradley-sources-modal
    :open="modalSource"
    :message="activeSourceMessage"
    :conversation-id="conversationId"
    :conversation-messages="props.messages"
    :uploaded-documents="uploadedDocuments"
    @close="sourceModalClose"
  />

  <bradley-report-modal
    :open="reportModalOpen"
    :conversation-id="conversationId"
    :message-id="selectedMessageForReport?.message_id ?? null"
    :existing-report="selectedMessageForReport?.report ?? null"
    @close="reportModalClose"
    @success="handleReportSuccess"
  />

  <bradley-dislike-modal
    :open="dislikeModalOpen"
    :conversation-id="conversationId"
    :message="selectedMessageForDislike"
    :message-index="selectedMessageIndexForDislike"
    @close="closeDislikeModal"
    @success="handleDislikeSuccess"
  />

  <bradley-delete-chat-modal
    :open="deleteModalOpen"
    @delete="$emit('delete')"
    @close="closeDeleteModalOpen"
  />
</template>

<style scoped>
.chat-file {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  background: #fafafe;
  padding-left: 32px;
  padding-right: 32px;
}

.chat-file__body {
  flex: 1;
  min-height: 0;
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: hidden;
}

.chat-file__messages {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.25rem;
  padding-bottom: 1.5rem;
}

.chat-file__messages-loader,
.chat-file__messages-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #6b7280;
  text-align: center;
  min-height: 120px;
}

.chat-file__messages-empty i {
  font-size: 1.5rem;
  color: #9ca3af;
}

.chat-file__messages-top-bar {
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: space-between;
  flex-shrink: 0;
  margin-top: 0.75rem;
  padding-bottom: 0.75rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.chat-file__messages-top-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.chat-file__datetime {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0;
  font-size: 0.88rem;
  color: #64748b;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.chat-file__link-button {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.85rem;
  border-radius: 0.85rem;
  border: none;
  background: transparent;
  font-size: 0.88rem;
  font-weight: 600;
  color: #25274f;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.chat-file__link-button:hover:not(:disabled) {
  background: rgba(37, 99, 235, 0.12);
}

.chat-file__link-button--danger {
  color: #dc2626;
}

.chat-file__link-button--danger:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.12);
}

.chat-file__link-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.chat-message {
  display: flex;
}

.chat-message--user {
  justify-content: flex-end;
  border-bottom: 1px solid #E5E7EB;
  padding-bottom: 15px;
}

.chat-message--assistant {
  justify-content: flex-start;
}

.chat-message__inner {
  display: flex;
  flex-direction: column;
}

.chat-message--assistant .chat-message__inner {
  width: 100%;
  align-items: flex-start;
}

.chat-message--user .chat-message__inner {
  max-width: 720px;
  align-items: flex-end;
}

.chat-message__user-pill {
  background: #E2E8F0;
  color: #25274F;
  padding: 0.65rem .9rem;
  border-radius: .5rem;
  font-size: 0.88rem;
  line-height: 1.45;
  box-shadow: none;
  display: inline-block;
}

.chat-message__content {
  display: block;
  width: 100%;
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  color: #334155;
  font-size: 0.95rem;
  line-height: 20px;
}

.chat-message--user .chat-message__content {
  width: auto;
  max-width: 100%;
}

.chat-message__content :deep(p) {
  margin: 0.75em 0;
}

.chat-message__content :deep(h1) {
  font-size: 1.5em;
  font-weight: 700;
  margin: 1em 0 0.5em;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.3em;
}

.chat-message__content :deep(h2) {
  font-size: 1.25em;
  font-weight: 700;
  margin: 0.9em 0 0.5em;
}

.chat-message__content :deep(h3) {
  font-size: 1.125em;
  font-weight: 700;
  margin: 0.8em 0 0.5em;
}

.chat-message__content :deep(ul),
.chat-message__content :deep(ol) {
  margin: 1em 0;
  padding-left: 2em;
}

.chat-message__content :deep(ul) {
  list-style-type: disc;
}

.chat-message__content :deep(ol) {
  list-style-type: decimal;
}

.chat-message__content :deep(li) {
  margin: 0.25em 0;
}

.chat-message__content :deep(strong) {
  font-weight: 700;
}

.chat-message__content :deep(em) {
  font-style: italic;
}

.chat-message__content :deep(hr) {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 1.5em 0;
}

.chat-message__content :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}

.chat-message__content :deep(a:hover) {
  color: #1d4ed8;
}

.chat-message__content :deep(code) {
  background-color: #f3f4f6;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-size: 0.9em;
  font-family: monospace;
}

.chat-message__content :deep(pre) {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 1em;
  overflow-x: auto;
  margin: 1em 0;
}

.chat-message__content :deep(pre code) {
  background: transparent;
  padding: 0;
}

.chat-message__content :deep(blockquote) {
  border-left: 4px solid #e5e7eb;
  padding-left: 1em;
  margin: 1em 0;
  color: #6b7280;
}

.chat-message__content :deep(.chat-message__citation) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.15rem;
  height: 1.15rem;
  margin: 0 0.1rem;
  padding: 0 0.25rem;
  border: 1px solid var(--p-primary-color, #1d4ed8);
  border-radius: 0.35rem;
  background: transparent;
  color: var(--p-primary-color, #1d4ed8);
  font-size: 0.7rem;
  line-height: 1;
  vertical-align: super;
  cursor: pointer;
}

.chat-message__content :deep(.chat-message__citation:hover) {
  background: var(--p-primary-color, #1d4ed8);
  color: #fff;
}

.chat-message__meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.chat-message__meta-value {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
  border-radius: 999px;
  padding: 0.15rem 0.65rem;
  text-transform: none;
  letter-spacing: 0;
}

.chat-message__timestamp {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-left: auto;
}

.chat-message--user .chat-message__timestamp {
  color: #6B7280;
  margin-left: 0;
}

.chat-file__typing-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  padding: 0.5rem 0.75rem;
  border-radius: 9999px;
  background-color: #f3f4f6;
}

.chat-file__typing-indicator i {
  font-size: 0.875rem;
}

.chat-file__typing-indicator--inline {
  align-self: flex-start;
  margin-top: 0.5rem;
}

.chat-file__footer {
  flex-shrink: 0;
  background: transparent;
  padding-bottom: 1.5rem;
  padding-top: 0.5rem;
  border-top: none;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.chat-file__input-wrapper {
  position: relative;
}

.chat-file__input-wrapper {
  position: relative;
  border: 1px solid #d1d5db;
  border-radius: 1rem;
  background-color: #ffffff;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.05);
  padding: 1rem 1.25rem;
}

.chat-file__input-wrapper--drag-active {
  border-color: #6366f1;
  background-color: rgba(238, 242, 255, 0.45);
}

.chat-file__composer-top {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.chat-file__staged-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.chat-file__upload-limits {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  margin: 0.75rem 0 0;
  text-align: left;
}

.chat-file__upload-limits-icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 0.875rem;
  height: 0.875rem;
  font-size: 0.875rem;
  line-height: 1;
  color: #a3a3c4;
}

.chat-file__upload-limits-icon::before {
  line-height: 1;
}

.chat-file__upload-limits-text {
  font-family: var(--font-inter, 'Inter', sans-serif);
  font-size: 12px;
  font-weight: 400;
  font-style: normal;
  line-height: 163%;
  letter-spacing: 0;
  color: #7a7a93;
}

.chat-file__textarea {
  width: 100%;
  border: none;
  padding: 0;
  resize: none;
  font-family: var(--font-inter, 'Inter', sans-serif);
  font-size: 16px;
  font-weight: 400;
  line-height: 163%;
  color: #64748b;
  min-height: 24px;
  max-height: 200px;
  background-color: transparent;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: hidden;
}

.chat-file__textarea::placeholder {
  font-family: var(--font-inter, 'Inter', sans-serif);
  font-size: 16px;
  font-weight: 400;
  line-height: 163%;
  letter-spacing: 0;
  color: #7a7a93;
}

.chat-file__textarea:focus {
  outline: none;
}

.chat-file__input-wrapper:focus-within {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.chat-file__textarea-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.chat-file__textarea-row .chat-file__textarea {
  flex: 1;
  min-width: 0;
  width: auto;
}

.chat-file__textarea-row :deep(.bradley-composer-recorder-preview) {
  flex: 1 1 auto;
  min-width: 0;
}

.chat-file__textarea-row .chat-file__stop-button {
  flex-shrink: 0;
}

.chat-file__input-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 8px;
  padding-top: 0;
  border-top: none;
}

.chat-file__tools {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.chat-file__tool-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #e5e7eb;
  color: #374151;
  border-radius: 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  padding: .45rem;
}

.chat-file__tool-pill-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 0.5rem;
  border: none;
  background: #d1d5db;
  color: #4b5563;
  font-size: 0.625rem;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.chat-file__tool-pill-remove:hover {
  background-color: #9ca3af;
  color: #111827;
}

.chat-file__send-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.chat-file__send-button,
.chat-file__stop-button,
.chat-file__voice-button {
  width: 32px;
  height: 32px;
  border-radius: 0.75rem;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: #4b5563;
  background-color: #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.chat-file__stop-button {
  color: #525262;
  background-color: #eeeef5;
}

.chat-file__stop-button:hover {
  background-color: #e4e2ee;
  color: #525262;
}

.chat-file__send-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.chat-file__send-button:not(:disabled):hover,
.chat-file__voice-button:hover {
  background-color: #e5e7eb;
  color: #1f2937;
}

.chat-file__voice-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.chat-file__voice-button--recording,
.chat-file__voice-button--recording:hover {
  color: #ffffff;
  background-color: #dc2626;
}

.chat-file__send-button--primary {
  border-radius: 0.5rem;
  background-color: #0000cc;
  color: #ffffff;
}

.chat-file__send-button--primary:not(:disabled):hover {
  background-color: #0000b3;
  color: #ffffff;
}

.chat-file__send-button--primary:disabled {
  opacity: 0.4;
}

/* Blinking cursor during streaming */
.chat-message__typing-cursor {
  display: inline-block;
  margin-left: 1px;
  animation: blink-cursor 1s step-end infinite;
  color: #6b7280;
  font-size: 0.9em;
}

@keyframes blink-cursor {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.chat-file__document-selector {
  margin-bottom: 1.5rem;
}

.chat-file__upload-area {
  border: 1px solid #d1d5db;
  border-radius: 0.75rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.chat-file__upload-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.chat-file__upload-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chat-file__upload-button i {
  font-size: 0.875rem;
}

.chat-file__upload-button--primary {
  background-color: var(--p-primary-500);
  color: #ffffff;
}

.chat-file__upload-button--primary:hover {
  background-color: var(--p-primary-600);
}

.chat-file__upload-button--secondary {
  background-color: #e5e7eb;
  color: #374151;
}

.chat-file__upload-button--secondary:hover {
  background-color: #d1d5db;
}

.chat-file__upload-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.chat-file__dropzone {
  border: 2px dashed #d1d5db;
  border-radius: 0.75rem;
  padding: 3rem 1rem;
  text-align: left;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.chat-file__dropzone--dragging {
  border-color: #4f46e5;
  background-color: #eef2ff;
}

.chat-file__dropzone-text {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

.chat-file__dropzone-hint {
  color: #9ca3af;
  font-size: 0.75rem;
  margin: 0.5rem 0 0 0;
}

.chat-file__dropzone-selected {
  margin-top: 0.75rem;
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.chat-file__dropzone-filename {
  color: #111827;
  font-weight: 600;
}

.document-multiselect :deep(.p-multiselect) {
  border-color: var(--p-primary-500);
  background-color: var(--p-primary-500);
}

.document-multiselect :deep(.p-multiselect-label) {
  color: #ffffff;
  font-weight: 400;
  font-size: 14px;
  font-style: normal;
}

.document-multiselect :deep(.p-multiselect-trigger) {
  color: #ffffff;
}

.document-multiselect :deep(.p-multiselect-panel .p-multiselect-item) {
  color: var(--p-primary-500);
  font-weight: 400;
  font-size: 14px;
  font-style: normal;
}

.chat-file__table-action {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  font-size: 0.875rem;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;
}

.chat-file__table-action i {
  color: inherit;
}

@media (max-width: 768px) {
  .chat-file {
    border-radius: 1.5rem;
  }

  .chat-file__body,
  .chat-file__footer {
    padding: 1.5rem;
  }

  .chat-file__input-actions {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>

<style>
html.dark .chat-file,
html.dark .chat-file__footer {
  background: #14143a;
}

html.dark .chat-file__messages-loader,
html.dark .chat-file__messages-empty {
  color: #a3a3c4;
}

html.dark .chat-file__messages-empty i {
  color: #7a7a93;
}

html.dark .chat-file__messages-top-bar {
  border-bottom-color: #2e2f5a;
}

html.dark .chat-file__datetime {
  color: #a3a3c4;
}

html.dark .chat-file__link-button {
  color: #c2c2e8;
}

html.dark .chat-file__link-button--danger {
  color: #f87171;
}

html.dark .chat-message--user {
  border-bottom-color: #2e2f5a;
}

html.dark .chat-message__user-pill {
  background: #25274f;
  color: #e0e0f9;
}

html.dark .chat-message__content {
  color: #e0e0f9;
}

html.dark .chat-message__content h1,
html.dark .chat-message__content hr {
  border-color: #2e2f5a;
}

html.dark .chat-message__content a {
  color: #8f8ff5;
}

html.dark .chat-message__content a:hover {
  color: #b3b3f8;
}

html.dark .chat-message__content code {
  background-color: #25274f;
  color: #e0e0f9;
}

html.dark .chat-message__content pre {
  background-color: #1a1a40;
  border-color: #2e2f5a;
}

html.dark .chat-message__content blockquote {
  border-left-color: #6c6cf0;
  color: #a3a3c4;
}

html.dark .chat-message__meta {
  color: #a3a3c4;
}

html.dark .chat-message__timestamp {
  color: #7a7a93;
}

html.dark .chat-message--user .chat-message__timestamp {
  color: #a3a3c4;
}

html.dark .chat-file__typing-indicator {
  color: #a3a3c4;
  background-color: #1a1a40;
}

html.dark .chat-file__input-wrapper {
  border-color: #2e2f5a;
  background-color: #1a1a40;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.25);
}

html.dark .chat-file__textarea,
html.dark .chat-file__textarea::placeholder {
  color: #c2c2e8;
}

html.dark .chat-file__textarea::placeholder {
  color: #7a7a93;
}

html.dark .chat-file__tool-pill {
  background-color: #25274f;
  color: #e0e0f9;
}

html.dark .chat-file__tool-pill-remove {
  background: #1a1a40;
  color: #a3a3c4;
}

html.dark .chat-file__tool-pill-remove:hover {
  background-color: #424252;
  color: #ffffff;
}

html.dark .chat-file__send-button {
  color: #c2c2e8;
  background-color: #1a1a40;
}

html.dark .chat-file__send-button--primary {
  background-color: #0000cc;
  color: #ffffff;
}

html.dark .chat-file__send-button--primary:not(:disabled):hover {
  background-color: #0000b3;
  color: #ffffff;
}

html.dark .chat-file__send-button:not(:disabled):hover {
  background-color: #25274f;
  color: #ffffff;
}

html.dark .chat-file__voice-button {
  color: #c2c2e8;
  background-color: #1a1a40;
}

html.dark .chat-file__voice-button:not(:disabled):hover {
  background-color: #25274f;
  color: #ffffff;
}

html.dark .chat-file__voice-button--recording,
html.dark .chat-file__voice-button--recording:hover {
  color: #ffffff;
  background-color: #dc2626;
}

html.dark .chat-file__disclaimer {
  color: #a3a3c4;
}

html.dark .chat-file__disclaimer-links a {
  color: #c2c2e8;
}

html.dark .chat-file__disclaimer-links a:hover {
  color: #b3b3f8;
}

html.dark .chat-file__upload-area {
  border-color: #2e2f5a;
}

html.dark .chat-file__upload-button--secondary {
  background-color: #25274f;
  color: #e0e0f9;
}

html.dark .chat-file__upload-button--secondary:hover {
  background-color: #424252;
}

html.dark .chat-file__dropzone {
  border-color: #2e2f5a;
}

html.dark .chat-file__dropzone--dragging {
  border-color: #6c6cf0;
  background-color: #1a1a40;
}

html.dark .chat-file__dropzone-text {
  color: #a3a3c4;
}

html.dark .chat-file__dropzone-hint {
  color: #7a7a93;
}

html.dark .chat-file__dropzone-selected {
  color: #e0e0f9;
}

html.dark .chat-file__dropzone-filename {
  color: #ffffff;
}
</style>
